import React from 'react';

import {Site} from 'src/lib/api/project';
import {ForecastTime, ForecastTimesBySource, Source} from 'src/lib/api/series';
import {addDays, closestForecastTimeWithinDay} from 'src/lib/dates';

import useForecastTimes from './useForecastTimes';
import {useForecastTimeSelection} from './useForecastTimeSelection';

export type ForecastTimesState = {
  // all sources
  forecastTimesBySource: ForecastTimesBySource | undefined;
  selectedForecastTimesBySource: ForecastTimesBySource;

  // primary source
  primaryForecastTimes: ForecastTime[] | undefined;
  selectedPrimaryForecastTimes: ForecastTime[];
  setSelectedPrimaryForecastTimes: (forecastTimes: ForecastTime[]) => void;
  onNextPrimaryForecastTimesChange: (callback: (forecastTimes: ForecastTime[]) => void) => void;
};

export function useSelectedForecastTimes(
  site: Site,
  dateRange: [Date, Date],
  horizonDays: number,
  primarySource: Source,
  forecastSources: Source[],
  mayUseLatestForecastTimeForSource: boolean = true,
  isStaging: boolean = false
): {
  primaryForecastTimes: ForecastTime[] | undefined;
  selectedPrimaryForecastTimes: ForecastTime[];
  forecastTimesBySource: ForecastTimesBySource | undefined;
  selectedForecastTimesBySource: ForecastTimesBySource;
  setSelectedPrimaryForecastTimes: (forecastTimes: ForecastTime[]) => void;
  onNextPrimaryForecastTimesChange: (
    callback: (primaryForecastTimes: ForecastTime[]) => void
  ) => void;
  forecastTimesAreLoading: boolean;
} {
  const [startDate, endDate] = dateRange;
  const forecastTimesRequest = useForecastTimes(
    site.id,
    forecastSources,
    addDays(startDate, -1 * horizonDays),
    endDate,
    isStaging
  );
  const forecastTimesAreLoading = forecastTimesRequest.isLoading;
  const forecastTimesBySource = forecastTimesRequest.data;

  const primaryForecastTimes = forecastTimesBySource && forecastTimesBySource[primarySource];

  const {
    selectedForecastTimes: selectedPrimaryForecastTimes,
    setSelectedForecastTimes: setSelectedPrimaryForecastTimes,
    onNextForecastTimesChange: onNextPrimaryForecastTimesChange,
  } = useForecastTimeSelection(
    primaryForecastTimes,
    dateRange,
    // A different site's (or staging mode's) forecast times are unrelated — drop the override.
    `${site.id}:${isStaging}`
  );

  /** Use the latest forecast time available for the source if the setting is
   * enabled via props and we’ve selected the latest forecast time for the
   * primary source. */
  const getUseLatestForecastTimeForSource = React.useCallback(
    (primaryForecastTime: ForecastTime) => {
      return !!(
        mayUseLatestForecastTimeForSource &&
        primaryForecastTimes &&
        primaryForecastTimes.length &&
        primaryForecastTime.initializationTime.getTime() ===
          primaryForecastTimes?.[0].initializationTime.getTime()
      );
    },
    [primaryForecastTimes, mayUseLatestForecastTimeForSource]
  );

  const selectedForecastTimesBySource = React.useMemo(() => {
    if (!forecastTimesBySource || selectedPrimaryForecastTimes.length === 0) {
      return {};
    }
    return getRelatedForecastTimesBySource(
      selectedPrimaryForecastTimes,
      forecastTimesBySource,
      getUseLatestForecastTimeForSource
    );
  }, [selectedPrimaryForecastTimes, forecastTimesBySource, getUseLatestForecastTimeForSource]);

  return {
    primaryForecastTimes,
    selectedPrimaryForecastTimes,
    forecastTimesBySource,
    selectedForecastTimesBySource,
    setSelectedPrimaryForecastTimes,
    onNextPrimaryForecastTimesChange,
    forecastTimesAreLoading,
  };
}

// Finds the input or otherwise nearest initialization time for each source in
// forecastTimesBySource to each issue time in referenceForecastTimes
function getRelatedForecastTimesBySource(
  referenceForecastTimes: ForecastTime[],
  forecastTimesBySource: ForecastTimesBySource,
  getUseLatestForecastTimeForSource: (referenceForecastTime: ForecastTime) => boolean
): ForecastTimesBySource {
  // Loop over the list of issue times for each source,
  return Object.entries(forecastTimesBySource).reduce((acc, [source, forecastTimesForSource]) => {
    const relatedForecastTimes: Set<ForecastTime> = new Set();
    // loop over the list of reference issue times (the ones we are comparing to)
    referenceForecastTimes.forEach((referenceForecastTime) => {
      // if we're in the "latest" shortcut view, naively use the latest of every source.
      // We do this so that users can view the most up to date weather forecasts,
      // regardless of what went into the models, especially during live flow events.
      if (getUseLatestForecastTimeForSource(referenceForecastTime)) {
        relatedForecastTimes.add(forecastTimesForSource[0]);
        return;
      }

      // check to see if the reference forecast time has inputs, and select those instead of nearest heuristic
      if (
        referenceForecastTime.inputInitializationTimes &&
        referenceForecastTime.inputInitializationTimes[source as Source]
      ) {
        // for a given source, get the init time that was the input for the reference (HF flow) forecast
        const inputInitTimeFromReferenceForecast =
          referenceForecastTime.inputInitializationTimes[source as Source];
        // search the list of forecast times returned by the API for that source & if we find the corresponding
        // init time, use that
        const inputForecastTime = forecastTimesBySource[source as Source]?.find(
          (ft) => ft.initializationTime.getTime() === inputInitTimeFromReferenceForecast!.getTime()
        );
        if (inputForecastTime) {
          relatedForecastTimes.add(inputForecastTime);
          return;
        }
      }

      // Select the issue time closest to the reference issue time if it is within one day
      const closestForecastTime = closestForecastTimeWithinDay(
        referenceForecastTime,
        forecastTimesForSource
      );
      if (closestForecastTime) {
        relatedForecastTimes.add(closestForecastTime);
      }
    });
    return {...acc, [source]: Array.from(relatedForecastTimes)};
  }, {});
}
